import React, { memo } from 'react'
import { Card, Grid, Typography } from '@mui/material'
import { EMBED_ANALYTICS_COLORS } from '../components'
import { translate } from '../i18n'
import { AnalyticsSummaryProps } from './AnalyticsSummary.types'

interface StatTile {
    label: string
    value: string
}

function AnalyticsSummary({ scans, embedded = false }: AnalyticsSummaryProps) {
    const tiles: StatTile[] = [
        {
            label: translate('projects.analytics.total_scans') as string,
            value: `${scans.total_scans}`,
        },
        {
            label: translate('projects.analytics.unique_users') as string,
            value: `${scans.unique_scans}`,
        },
    ]

    if (scans.total_clicks !== null) {
        tiles.push({
            label: translate('projects.analytics.total_clicks') as string,
            value: `${scans.total_clicks}`,
        })
    }

    if (embedded) {
        // Wide horizontal bars: label left, large number right, thin weight.
        return (
            <Grid container spacing={1.5}>
                {tiles.map((tile) => (
                    <Grid item xs={12 / tiles.length} key={tile.label}>
                        <Card
                            variant="elevation"
                            elevation={0}
                            sx={{
                                p: 2.5,
                                width: '100%',
                                maxHeight: 120,
                                boxSizing: 'border-box',
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                bgcolor: EMBED_ANALYTICS_COLORS.data,
                                color: EMBED_ANALYTICS_COLORS.onData,
                            }}
                        >
                            <Typography
                                component="div"
                                color="inherit"
                                sx={{
                                    fontWeight: 500,
                                    fontSize: { xs: '1.5rem' },
                                    lineHeight: 1.1,
                                    opacity: 0.9,
                                }}
                            >
                                {`${tile.label}:`}
                            </Typography>
                            <Typography
                                component="div"
                                color="inherit"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: { xs: '3rem' },
                                    lineHeight: 1,
                                }}
                            >
                                {tile.value}
                            </Typography>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        )
    }

    return (
        <Grid container spacing={1}>
            {tiles.map((tile) => (
                <Grid item xs={12 / tiles.length} key={tile.label}>
                    <Card variant="outlined" sx={{ p: 1, height: '100%' }}>
                        <Typography
                            variant="caption"
                            component="div"
                            color="text.secondary"
                            align="center"
                        >
                            {tile.label}
                        </Typography>
                        <Typography variant="h6" component="div" align="center">
                            {tile.value}
                        </Typography>
                    </Card>
                </Grid>
            ))}
        </Grid>
    )
}

export default memo(AnalyticsSummary)
